import {
  THUMBNAIL_MAX_EDGE,
  THUMBNAIL_QUALITY,
  WEBP_QUALITY,
  fitWithin,
  isHeic,
  isVideo,
  resolveTakenAt,
  storedMimeFor,
} from "./format";

export interface PreparedMedia {
  type: "photo" | "video";
  /** 保存する本体。HEIC のときだけ変換後の WebP、それ以外は元のファイル */
  original: Blob;
  originalMime: string;
  thumbnail: Blob;
  width: number;
  height: number;
  durationSeconds: number | null;
  takenAt: string;
  byteSize: number;
}

/** exifr の戻りは型が付かないため、必要な項目だけ実行時に取り出す */
function exifDateFrom(parsed: unknown): Date | undefined {
  if (typeof parsed !== "object" || parsed === null) return undefined;
  const value: unknown = (parsed as Record<string, unknown>).DateTimeOriginal;
  return value instanceof Date ? value : undefined;
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob === null) reject(new Error("画像の書き出しに失敗しました"));
        else resolve(blob);
      },
      mime,
      quality,
    );
  });
}

function drawScaled(source: CanvasImageSource, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (context === null) throw new Error("Canvas を初期化できません");
  context.drawImage(source, 0, 0, width, height);
  return canvas;
}

async function loadImage(blob: Blob): Promise<ImageBitmap> {
  return createImageBitmap(blob);
}

/** 動画の読み込みを諦めるまでの時間 */
const VIDEO_READ_TIMEOUT_MS = 30_000;

/** 描き終わるのを待つ上限。requestVideoFrameCallback が来ない環境向けの保険 */
const FRAME_WAIT_MS = 400;

/** 1コマ描かれるのを待つ。seeked の直後はまだ canvas に写らないことがある */
function nextPaintedFrame(video: HTMLVideoElement): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    if (typeof video.requestVideoFrameCallback === "function") {
      video.requestVideoFrameCallback(() => {
        finish();
      });
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(finish);
      });
    }
    setTimeout(finish, FRAME_WAIT_MS);
  });
}

/**
 * 動画からサムネイルと寸法・長さを取り出す。
 *
 * サーバー側でのサムネイル生成ジョブは Phase1 では持たない方針のため、
 * ここで抽出する(→ 要件定義 7章)。
 *
 * **描き出しまでを1つの関数の中で完結させる。** 以前は video を返して
 * 呼び出し側で描いていたが、その前に `revokeObjectURL` を済ませていたため、
 * 描く時点で元が失われて空のサムネイルになっていた。
 *
 * iOS は画面に載っていない video のコマを用意しないことがあるので、
 * 見えない大きさで DOM に入れてから読む。
 */
async function readVideo(
  file: File,
): Promise<{ thumbnail: Blob; width: number; height: number; duration: number }> {
  const video = document.createElement("video");
  video.preload = "auto";
  video.muted = true;
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  video.style.cssText = "position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0";

  const url = URL.createObjectURL(file);
  document.body.append(video);

  try {
    await new Promise<void>((resolve, reject) => {
      // タイムアウトが無いと、デコードできない動画で seeked も error も
      // 発火せず永久に待ち続ける。利用者からは「遅い」ではなく「固まった」に見える
      const timer = setTimeout(() => {
        finish(() => {
          reject(
            new Error(
              "動画を読み込めませんでした。時間がかかりすぎているか、形式が対応していません",
            ),
          );
        });
      }, VIDEO_READ_TIMEOUT_MS);

      let settled = false;
      const finish = (action: () => void) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        action();
      };

      // loadeddata まで待つ。metadata だけではコマがまだ無い
      video.onloadeddata = () => {
        // 完全な先頭は真っ黒なことがあるので、少しだけ進めた位置を使う
        const target = Number.isFinite(video.duration) ? Math.min(0.1, video.duration / 2) : 0;
        if (target <= 0) {
          finish(resolve);
          return;
        }
        video.currentTime = target;
      };
      video.onseeked = () => {
        finish(resolve);
      };
      video.onerror = () => {
        finish(() => {
          reject(new Error("この動画は再生できません。形式が対応していない可能性があります"));
        });
      };

      video.src = url;
      video.load();
    });

    await nextPaintedFrame(video);

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width === 0 || height === 0) {
      throw new Error("動画の大きさを取得できませんでした");
    }

    const size = fitWithin(width, height, THUMBNAIL_MAX_EDGE);
    const thumbnail = await canvasToBlob(
      drawScaled(video, size.width, size.height),
      "image/webp",
      THUMBNAIL_QUALITY,
    );
    return { thumbnail, width, height, duration: video.duration };
  } finally {
    video.removeAttribute("src");
    video.load();
    video.remove();
    URL.revokeObjectURL(url);
  }
}

/**
 * ファイル1件を保存できる形に整える。
 *
 * 変換・EXIF抽出・サムネイル生成をクライアント側で行う。
 * サーバーに処理を持たせない代わりに、端末の負荷はここに集中する。
 */
export async function prepareMedia(file: File): Promise<PreparedMedia> {
  const mime = storedMimeFor(file);
  if (mime === null) {
    throw new Error(`対応していない形式です: ${file.type === "" ? file.name : file.type}`);
  }

  if (isVideo(file)) {
    const { thumbnail, width, height, duration } = await readVideo(file);

    return {
      type: "video",
      original: file,
      originalMime: mime,
      thumbnail,
      width,
      height,
      durationSeconds: Number.isFinite(duration) ? duration : null,
      // 動画のEXIFは扱いが揃わないため、更新日時を撮影日時として使う
      takenAt: resolveTakenAt(undefined, file.lastModified),
      byteSize: file.size,
    };
  }

  // EXIF は変換前の元ファイルから読む(変換すると失われる)。
  // EXIF が無い・壊れている写真は珍しくないので、失敗しても止めずに撮影日時だけ諦める
  const { default: exifr } = await import("exifr");
  const parsed: unknown = await exifr.parse(file, ["DateTimeOriginal"]).catch(() => undefined);
  const exifDate = exifDateFrom(parsed);

  let original: Blob = file;
  if (isHeic(file)) {
    // PNG を挟むのは可逆だから。非可逆な圧縮を WebP の1回だけに抑える
    // HEICデコーダは数MBある。HEICを選んだときだけ読み込み、
    // タイムラインを見るだけの利用者には配らない
    const { heicTo } = await import("heic-to");
    const png: unknown = await heicTo({ blob: file, type: "image/png" });
    if (!(png instanceof Blob)) {
      throw new Error("HEIC の変換に失敗しました");
    }
    const bitmap = await loadImage(png);
    original = await canvasToBlob(
      drawScaled(bitmap, bitmap.width, bitmap.height),
      "image/webp",
      WEBP_QUALITY,
    );
    bitmap.close();
  }

  const bitmap = await loadImage(original);
  const thumbSize = fitWithin(bitmap.width, bitmap.height, THUMBNAIL_MAX_EDGE);
  const thumbnail = await canvasToBlob(
    drawScaled(bitmap, thumbSize.width, thumbSize.height),
    "image/webp",
    THUMBNAIL_QUALITY,
  );
  const { width, height } = bitmap;
  bitmap.close();

  return {
    type: "photo",
    original,
    originalMime: mime,
    thumbnail,
    width,
    height,
    durationSeconds: null,
    takenAt: resolveTakenAt(exifDate, file.lastModified),
    byteSize: original.size,
  };
}
